import React, { useEffect, useState } from 'react';
import { DEPARTMENTS } from '../utils/roles';
import { roleAPI } from '../services/api';
import './RoleBasedEmployeeForm.css';

const RoleBasedEmployeeForm = ({ onSubmit, onCancel }) => {
  const [availableRoles, setAvailableRoles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    department: '',
    designation: '',
    dateOfJoining: new Date().toISOString().split('T')[0],
    salary: '',
    reportingTo: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await roleAPI.getAll();
        setAvailableRoles(response?.data || []);
      } catch (error) {
        console.error('Error fetching roles:', error);
        setAvailableRoles([]);
      }
    };

    fetchRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }
    if (!formData.role) newErrors.role = 'Role is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.designation) newErrors.designation = 'Designation is required';
    if (!formData.salary || formData.salary <= 0) newErrors.salary = 'Valid salary is required';
    if (!formData.password || formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    onSubmit({ ...formData });
  };

  const selectedRole = availableRoles.find((role) => role._id === formData.role);

  return (
    <form onSubmit={handleSubmit} className="role-based-form">
      <div className="form-section">
        <h4><i className="fas fa-user"></i> Basic Information</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Full Name <span className="required">*</span></label>
            <input
              type="text"
              name="name"
              className={`form-input ${errors.name ? 'error' : ''}`}
              value={formData.name}
              onChange={handleChange}
              placeholder="John Doe"
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>
          <div className="form-group">
            <label>Email <span className="required">*</span></label>
            <input
              type="email"
              name="email"
              className={`form-input ${errors.email ? 'error' : ''}`}
              value={formData.email}
              onChange={handleChange}
              placeholder="john.doe@company.com"
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Phone <span className="required">*</span></label>
            <input
              type="tel"
              name="phone"
              className={`form-input ${errors.phone ? 'error' : ''}`}
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 234-567-8900"
            />
            {errors.phone && <span className="error-text">{errors.phone}</span>}
          </div>
          <div className="form-group">
            <label>Date of Joining <span className="required">*</span></label>
            <input
              type="date"
              name="dateOfJoining"
              className="form-input"
              value={formData.dateOfJoining}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4><i className="fas fa-briefcase"></i> Role & Department</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Role <span className="required">*</span></label>
            <select
              name="role"
              className={`form-input ${errors.role ? 'error' : ''}`}
              value={formData.role}
              onChange={handleChange}
            >
              <option value="">Select Role</option>
              {availableRoles.map(role => (
                <option key={role._id} value={role._id}>
                  {role.name}
                </option>
              ))}
            </select>
            {errors.role && <span className="error-text">{errors.role}</span>}
            {formData.role && (
              <div className="role-info">
                <i className="fas fa-info-circle"></i>
                <span>Level: {selectedRole?.level || '-'} | Permissions: {(selectedRole?.permissions || []).length}</span>
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Department <span className="required">*</span></label>
            <select
              name="department"
              className={`form-input ${errors.department ? 'error' : ''}`}
              value={formData.department}
              onChange={handleChange}
            >
              <option value="">Select Department</option>
              {DEPARTMENTS.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            {errors.department && <span className="error-text">{errors.department}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Designation <span className="required">*</span></label>
            <input
              type="text"
              name="designation"
              className={`form-input ${errors.designation ? 'error' : ''}`}
              value={formData.designation}
              onChange={handleChange}
              placeholder="e.g., Sales Executive"
            />
            {errors.designation && <span className="error-text">{errors.designation}</span>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4><i className="fas fa-dollar-sign"></i> Compensation</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Annual Salary (USD) <span className="required">*</span></label>
            <input
              type="number"
              name="salary"
              className={`form-input ${errors.salary ? 'error' : ''}`}
              value={formData.salary}
              onChange={handleChange}
              placeholder="50000"
              min="0"
            />
            {errors.salary && <span className="error-text">{errors.salary}</span>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4><i className="fas fa-lock"></i> Security</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Password <span className="required">*</span></label>
            <div className="password-input">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className={`form-input ${errors.password ? 'error' : ''}`}
                value={formData.password}
                onChange={handleChange}
                placeholder="Minimum 6 characters"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i className={`fas fa-eye${showPassword ? '-slash' : ''}`}></i>
              </button>
            </div>
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>
          <div className="form-group">
            <label>Confirm Password <span className="required">*</span></label>
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter password"
            />
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          <i className="fas fa-times"></i> Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          <i className="fas fa-user-plus"></i> Create Employee
        </button>
      </div>
    </form>
  );
};

export default RoleBasedEmployeeForm;
